import React, { useState } from 'react';
import {
  User, Phone, Headphones, Wrench, MessageCircle,
  CheckCircle2, AlertTriangle, ChevronRight, ChevronDown,
} from 'lucide-react';
import { toast } from 'sonner';

/* ─── Design Tokens ─── */
const PANEL_BG = '#171411';
const SURFACE = '#1e1b16';
const BORDER = '#2a2620';
const MONO = "'DM Mono', monospace";
/* Panel headings render at 18px — under the 1.375rem display floor — so they take
   the mono body face at 500 rather than the display serif. */
const DISPLAY = 'var(--s8-font-mono)';
const BODY = "'Barlow', sans-serif";

const GREEN = '#5a9a6d';
const AMBER = '#E6A23C';
const MUTED = '#a39889';
const TEXT = '#F0EBE0';

/* Relative-date helper — mirrors /app/frontend/src/components/RiderUpload.jsx
   (kept inline to avoid cross-component imports for a single usage). */
function formatUploadedDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  const diffDays = Math.floor((now - d) / (1000 * 60 * 60 * 24));
  if (diffDays <= 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) {
    const w = Math.floor(diffDays / 7);
    return w === 1 ? '1 week ago' : `${w} weeks ago`;
  }
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

const TECH_RIDER_UPDATED_ISO = '2026-04-08';

/* ═══════════════════════════════════════════════════
   KEY CONTACTS
   ═══════════════════════════════════════════════════ */

const DEMO_CONTACTS = [
  { icon: User,       name: 'Sophie',  role: 'Artist',    phone: '+31 6 1234 5678',  assigned: true, whatsapp: true },
  { icon: Phone,      name: 'Cinetol', role: 'Booker', phone: '+31 20 248 0186', assigned: true, whatsapp: false },
  { icon: Headphones, name: 'Maarten', role: 'FOH',       phone: '+31 6 8765 4321',  assigned: true, whatsapp: false },
  { icon: Wrench,     name: null,      role: 'Stage Host', phone: null,               assigned: false, whatsapp: false },
];

function ContactTile({ contact }) {
  const Icon = contact.icon;
  const isUnassigned = !contact.assigned;

  const handleTap = () => {
    if (!contact.phone) return;
    if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
      window.location.href = `tel:${contact.phone.replace(/\s/g, '')}`;
    } else {
      navigator.clipboard.writeText(contact.phone).then(() => {
        toast.success(`Copied ${contact.phone}`);
      });
    }
  };

  const waNumber = contact.phone ? contact.phone.replace(/\s/g, '').replace('+', '') : '';

  return (
    <div className="relative">
      {contact.whatsapp && contact.phone && (
        <a
          href={`https://wa.me/${waNumber}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="whatsapp-btn"
          className="absolute top-1.5 right-1.5 z-10 w-6 h-6 rounded-full flex items-center justify-center transition-all hover:scale-110"
          style={{ background: '#25D366' }}
          onClick={(e) => e.stopPropagation()}
        >
          <MessageCircle size={12} color="#fff" />
        </a>
      )}
      <button
        onClick={handleTap}
        disabled={isUnassigned}
        data-testid={`contact-tile-${contact.role.toLowerCase().replace(/\s/g, '-')}`}
        className="flex flex-col items-center gap-1.5 p-3 rounded-xl transition-all text-center min-w-0 w-full"
        style={{
          background: isUnassigned ? 'transparent' : SURFACE,
          border: `1px solid ${isUnassigned ? '#2a262066' : BORDER}`,
          opacity: isUnassigned ? 0.45 : 1,
          cursor: isUnassigned ? 'default' : 'pointer',
        }}
      >
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center"
          style={{ background: isUnassigned ? '#1a1714' : `${AMBER}14`, border: `1px solid ${isUnassigned ? BORDER : `${AMBER}30`}` }}
        >
          <Icon size={16} color={isUnassigned ? MUTED : AMBER} />
        </div>
        <span style={{ fontFamily: BODY, fontSize: 13, fontWeight: 600, color: isUnassigned ? MUTED : TEXT }} className="truncate w-full">
          {isUnassigned ? 'Unassigned' : contact.name}
        </span>
        <span style={{ fontFamily: MONO, fontSize: 10, color: MUTED, letterSpacing: 0.3 }}>
          {isUnassigned ? 'Assign first' : contact.role}
        </span>
        {contact.phone && (
          <span style={{ fontFamily: MONO, fontSize: 10, color: '#888' }} className="truncate w-full">
            {contact.phone}
          </span>
        )}
      </button>
    </div>
  );
}

export function KeyContacts() {
  return (
    <div
      data-testid="key-contacts"
      className="mb-6 overflow-hidden"
      style={{
        background: PANEL_BG,
        borderRadius: 16,
        border: '1px solid rgba(212, 169, 90, 0.12)',
      }}
    >
      <div className="px-5 py-4" style={{ borderBottom: `1px solid ${BORDER}` }}>
        <h2 style={{ fontFamily: DISPLAY, fontWeight: 500, fontSize: 18, letterSpacing: '0.04em', color: TEXT }}>
          KEY CONTACTS
        </h2>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 p-4" style={{ background: SURFACE }}>
        {DEMO_CONTACTS.map((c) => (
          <ContactTile key={c.role} contact={c} />
        ))}
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════
   TECH RIDER (collapsible)
   ═══════════════════════════════════════════════════ */

const RIDER_MATCHES = [
  { label: 'FOH console',  detail: 'Allen & Heath SQ-5 available',         status: 'ok' },
  { label: 'Monitors',     detail: '3 wedges, 2 mixes (rider asks 2)',     status: 'ok' },
  { label: 'DI boxes',     detail: 'Need 2 active DIs — venue has 1',      status: 'warn' },
  { label: 'Power',        detail: 'Standard 16A sufficient',              status: 'ok' },
  { label: 'Lighting',     detail: 'House rig — no programming needed',    status: 'ok' },
];

export function TechRider({ reviewed, onMarkReviewed }) {
  const [open, setOpen] = useState(false);

  return (
    <div
      data-testid="tech-rider"
      className="mb-6 overflow-hidden"
      style={{
        background: PANEL_BG,
        borderRadius: 16,
        border: '1px solid rgba(212, 169, 90, 0.12)',
      }}
    >
      {/* Collapsible header */}
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4"
        data-testid="tech-rider-toggle"
        type="button"
      >
        <div className="flex flex-col items-start">
          <h2 style={{ fontFamily: DISPLAY, fontWeight: 500, fontSize: 18, letterSpacing: '0.04em', color: TEXT }}>
            TECH RIDER
          </h2>
          {!open && (
            <span style={{ fontFamily: BODY, fontSize: 13, color: '#8a8278', marginTop: 2 }}>
              <AlertTriangle size={12} color={AMBER} className="inline mr-1" style={{ verticalAlign: '-1px' }} />
              1 gap: DI boxes · Updated 8 Apr
            </span>
          )}
        </div>
        <ChevronDown
          size={18}
          color="#888"
          className="shrink-0"
          style={{ transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
        />
      </button>

      {/* Expanded content */}
      {open && (
        <div style={{ background: SURFACE, borderTop: `1px solid ${BORDER}` }}>
          <div className="flex items-center justify-between px-5 pt-3">
            <p style={{ fontFamily: MONO, fontSize: 11, color: MUTED }}>
              Updated 8 Apr{(() => {
                const rel = formatUploadedDate(TECH_RIDER_UPDATED_ISO);
                return rel ? ` (${rel})` : '';
              })()} · v2
            </p>
            <button
              data-testid="tech-rider-view-pdf"
              className="flex items-center gap-1 px-2 py-1 text-xs transition-all hover:opacity-70"
              style={{
                fontFamily: MONO, fontSize: 11, letterSpacing: 0.3,
                color: AMBER, background: 'transparent', border: 'none',
              }}
            >
              View PDF
              <ChevronRight size={12} />
            </button>
          </div>
          <p className="px-5 pt-3 pb-1" style={{ fontFamily: BODY, fontSize: 12, fontWeight: 600, color: '#a39889', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            Venue capability match
          </p>
          {RIDER_MATCHES.map((item, i) => {
            const isOk = item.status === 'ok';
            const StatusIcon = isOk ? CheckCircle2 : AlertTriangle;
            const color = isOk ? GREEN : AMBER;
            return (
              <div
                key={item.label}
                data-testid={`rider-match-${i}`}
                className="flex items-center gap-3 py-2.5 px-5"
                style={{ borderBottom: i < RIDER_MATCHES.length - 1 ? `1px solid ${BORDER}` : 'none' }}
              >
                <StatusIcon size={14} color={color} className="shrink-0" />
                <span style={{ fontFamily: BODY, fontSize: 13, fontWeight: 600, color: TEXT, minWidth: 110 }}>
                  {item.label}
                </span>
                <span style={{ fontFamily: MONO, fontSize: 11, color: MUTED }} className="truncate">
                  {item.detail}
                </span>
              </div>
            );
          })}
          <div className="px-5 py-4 flex justify-end">
            <button
              data-testid="mark-rider-reviewed"
              onClick={onMarkReviewed}
              disabled={reviewed}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all"
              style={{
                fontFamily: BODY,
                background: reviewed ? `${GREEN}20` : AMBER,
                color: reviewed ? GREEN : '#0E1110',
                border: reviewed ? `1px solid ${GREEN}40` : 'none',
                cursor: reviewed ? 'default' : 'pointer',
                fontWeight: 600,
              }}
            >
              {reviewed ? (
                <>
                  <CheckCircle2 size={14} />
                  Reviewed
                </>
              ) : (
                'Mark reviewed'
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
